import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
    PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, 
    CartesianGrid, Tooltip, Legend, AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import { 
    TrendingUp, TrendingDown, Users, DollarSign, Shield, 
    CheckCircle, XCircle, Clock, AlertTriangle, Target,
    BarChart3, PieChartIcon, Activity, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { getEstadisticasAnalisis } from '../lib/api';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const COLORS = {
    primary: '#10b981',
    secondary: '#3b82f6',
    accent: '#f59e0b',
    danger: '#ef4444',
    purple: '#8b5cf6',
    pink: '#ec4899',
    teal: '#14b8a6',
    orange: '#f97316'
};

const RISK_COLORS = {
    'Muy bajo': COLORS.primary,
    'Bajo': '#22c55e',
    'Medio': COLORS.accent,
    'Alto': COLORS.orange,
    'Muy alto': COLORS.danger
};

const TIPO_COLORS = {
    'Personal': COLORS.secondary,
    'Hipotecario': COLORS.purple,
    'Vehiculo': COLORS.orange,
    'Empresarial': COLORS.primary,
    'Consumo': COLORS.accent,
    'Educativo': COLORS.pink
};

const ESTADO_COLORS = {
    'Pendiente': COLORS.accent,
    'Aprobado': COLORS.primary,
    'Rechazado': COLORS.danger,
    'Condicional': COLORS.orange,
    'Convertido': COLORS.secondary
};

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "primary" }) => {
    const colorClasses = {
        primary: "bg-primary/10 text-primary",
        green: "bg-green-500/10 text-green-500",
        blue: "bg-blue-500/10 text-blue-500",
        yellow: "bg-yellow-500/10 text-yellow-500",
        red: "bg-red-500/10 text-red-500",
        purple: "bg-purple-500/10 text-purple-500"
    };

    return (
        <Card className="overflow-hidden">
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">{title}</p>
                        <p className="text-3xl font-bold">{value}</p>
                        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
                        {trend && (
                            <div className={`flex items-center gap-1 text-xs ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                                {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {trendValue}
                            </div>
                        )}
                    </div>
                    <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-background border rounded-lg shadow-lg p-3">
                <p className="font-medium">{label || payload[0].name}</p>
                {payload.map((entry, index) => (
                    <p key={index} className="text-sm" style={{ color: entry.color }}>
                        {entry.name}: {typeof entry.value === 'number' && entry.value > 1000 
                            ? formatCurrency(entry.value) 
                            : entry.value}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

export default function AnalisisDashboardPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const { data } = await getEstadisticasAnalisis();
            setStats(data);
        } catch (error) {
            toast.error('Error al cargar estadísticas');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-80" />)}
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">No hay datos disponibles</p>
            </div>
        );
    }

    const { resumen, graficos } = stats;

    // Score gauge data
    const scoreGaugeData = [{
        name: 'Score',
        value: resumen.score_promedio,
        fill: resumen.score_promedio >= 65 ? COLORS.primary : 
              resumen.score_promedio >= 50 ? COLORS.accent : COLORS.danger
    }];

    return (
        <div className="space-y-6" data-testid="analisis-dashboard">
            {/* Header */}
            <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Dashboard de Análisis Crediticio
                </h1>
                <p className="text-muted-foreground mt-1">
                    Monitoreo de solicitudes y evaluación de riesgo
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total Solicitudes"
                    value={resumen.total_solicitudes}
                    subtitle="Todas las solicitudes"
                    icon={Users}
                    color="blue"
                />
                <StatCard
                    title="Tasa de Aprobación"
                    value={`${resumen.tasa_aprobacion}%`}
                    subtitle={`${resumen.tasa_rechazo}% rechazadas`}
                    icon={CheckCircle}
                    color="green"
                />
                <StatCard
                    title="Score Promedio"
                    value={resumen.score_promedio}
                    subtitle="Puntuación crediticia"
                    icon={Target}
                    color={resumen.score_promedio >= 65 ? "green" : resumen.score_promedio >= 50 ? "yellow" : "red"}
                />
                <StatCard
                    title="Monto Aprobado"
                    value={formatCurrency(resumen.monto_total_aprobado)}
                    subtitle={`De ${formatCurrency(resumen.monto_total_solicitado)} solicitado`}
                    icon={DollarSign}
                    color="primary"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Score Gauge + Distribution */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Activity className="w-5 h-5 text-primary" />
                            Distribución de Scores
                        </CardTitle>
                        <CardDescription>
                            Puntuación crediticia de solicitudes
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center gap-6">
                            {/* Gauge */}
                            <div className="w-32 h-32 relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadialBarChart 
                                        cx="50%" 
                                        cy="50%" 
                                        innerRadius="60%" 
                                        outerRadius="100%" 
                                        data={scoreGaugeData}
                                        startAngle={180}
                                        endAngle={0}
                                    >
                                        <RadialBar
                                            background
                                            dataKey="value"
                                            cornerRadius={10}
                                        />
                                    </RadialBarChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-2xl font-bold">{resumen.score_promedio}</span>
                                    <span className="text-xs text-muted-foreground">Promedio</span>
                                </div>
                            </div>
                            
                            {/* Bar distribution */}
                            <div className="flex-1 h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={graficos.distribucion_scores} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                                        <XAxis type="number" hide />
                                        <YAxis dataKey="rango" type="category" width={50} tick={{ fontSize: 11 }} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar 
                                            dataKey="cantidad" 
                                            fill={COLORS.primary} 
                                            radius={[0, 4, 4, 0]}
                                            name="Solicitudes"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Por Nivel de Riesgo */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-accent" />
                            Nivel de Riesgo
                        </CardTitle>
                        <CardDescription>
                            Clasificación por riesgo crediticio
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={graficos.por_riesgo.filter(r => r.cantidad > 0)}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="cantidad"
                                        nameKey="nivel"
                                        label={({ nivel, percent }) => `${nivel} ${(percent * 100).toFixed(0)}%`}
                                        labelLine={false}
                                    >
                                        {graficos.por_riesgo.filter(r => r.cantidad > 0).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={RISK_COLORS[entry.nivel] || COLORS.primary} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Por Tipo de Préstamo */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-secondary" />
                            Por Tipo de Préstamo
                        </CardTitle>
                        <CardDescription>
                            Solicitudes y montos por categoría
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={graficos.por_tipo}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                                    <XAxis dataKey="tipo" tick={{ fontSize: 11 }} />
                                    <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 11 }} />
                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar yAxisId="left" dataKey="cantidad" fill={COLORS.secondary} name="Cantidad" radius={[4, 4, 0, 0]} />
                                    <Bar yAxisId="right" dataKey="monto" fill={COLORS.primary} name="Monto" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Estado de Solicitudes */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <PieChartIcon className="w-5 h-5 text-purple-500" />
                            Estado de Solicitudes
                        </CardTitle>
                        <CardDescription>
                            Distribución por estado actual
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={graficos.por_estado.filter(e => e.cantidad > 0)}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={100}
                                        dataKey="cantidad"
                                        nameKey="estado"
                                        label={({ estado, cantidad }) => `${estado}: ${cantidad}`}
                                    >
                                        {graficos.por_estado.filter(e => e.cantidad > 0).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={ESTADO_COLORS[entry.estado] || COLORS.primary} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Tendencia Mensual */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-primary" />
                            Tendencia de Solicitudes
                        </CardTitle>
                        <CardDescription>
                            Evolución mensual de solicitudes
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={graficos.tendencia_mensual}>
                                    <defs>
                                        <linearGradient id="colorCantidad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area 
                                        type="monotone" 
                                        dataKey="cantidad" 
                                        stroke={COLORS.primary} 
                                        fillOpacity={1}
                                        fill="url(#colorCantidad)"
                                        name="Solicitudes"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Garantías */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-accent" />
                            Garantías
                        </CardTitle>
                        <CardDescription>
                            Con vs Sin garantía
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={graficos.garantia}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="cantidad"
                                        nameKey="tipo"
                                    >
                                        <Cell fill={COLORS.primary} />
                                        <Cell fill={COLORS.accent} />
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex justify-center gap-6 mt-4">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-primary">{resumen.con_garantia}</p>
                                <p className="text-xs text-muted-foreground">Con Garantía</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-accent">{resumen.sin_garantia}</p>
                                <p className="text-xs text-muted-foreground">Sin Garantía</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Key Metrics Summary */}
            <Card className="bg-gradient-to-r from-primary/10 to-accent/10">
                <CardContent className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <Percent className="w-5 h-5 text-primary" />
                                <span className="text-sm font-medium">Ratio Aprobación</span>
                            </div>
                            <p className="text-3xl font-bold text-primary">{resumen.tasa_aprobacion}%</p>
                        </div>
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <Target className="w-5 h-5 text-secondary" />
                                <span className="text-sm font-medium">Score Promedio</span>
                            </div>
                            <p className="text-3xl font-bold text-secondary">{resumen.score_promedio}</p>
                        </div>
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <DollarSign className="w-5 h-5 text-accent" />
                                <span className="text-sm font-medium">Monto Promedio</span>
                            </div>
                            <p className="text-3xl font-bold text-accent">
                                {resumen.total_solicitudes > 0 
                                    ? formatCurrency(resumen.monto_total_solicitado / resumen.total_solicitudes)
                                    : '$0'}
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <Shield className="w-5 h-5 text-purple-500" />
                                <span className="text-sm font-medium">% Con Garantía</span>
                            </div>
                            <p className="text-3xl font-bold text-purple-500">
                                {resumen.total_solicitudes > 0 
                                    ? Math.round(resumen.con_garantia / resumen.total_solicitudes * 100)
                                    : 0}%
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
